import moment from "moment";
import { fillZero } from "./fill_zero";
import { WeekRow } from "./types";
export { WeekRow };
const API_DATETIME_FORMAT: string = "YYYYMMDDHHmmss";

// [moment](https://momentjs.com) の wrapper クラス
export class Moment {
  private moment: moment.Moment;
  private format: string;

  constructor(
    format: string = "YYYY-MM-DD HH:mm:ss",
    dateStr?: string,
    currentFormat?: string
  ) {
    moment.locale("ja");
    this.format = format;
    if (dateStr && currentFormat) {
      this.moment = moment(dateStr, currentFormat);
    } else {
      this.moment = dateStr ? moment(dateStr, this.format) : moment();
    }
  }

  // constructor にて何も指定していなければ、関数が呼び出された時刻を返却する
  getFormatDate(): string {
    return this.moment.format(this.format);
  }

  getStartOf(unit: moment.unitOfTime.StartOf): string {
    return this.moment.startOf(unit).format(this.format);
  }

  getEndOf(unit: moment.unitOfTime.StartOf): string {
    return this.moment.endOf(unit).format(this.format);
  }

  getDate(): number {
    return this.moment.date();
  }

  getDay(): number {
    return this.moment.day();
  }

  getWeekStartDay(): string {
    return this.moment.startOf("week").format(this.format);
  }

  getWeekEndDay(): string {
    return this.moment.endOf("week").format(this.format);
  }

  setDate(date: number) {
    this.moment.date(date);
  }

  addDate(num: number, unit: moment.DurationInputArg2): string {
    return this.moment.add(num, unit).format(this.format);
  }

  isValid(date: string): boolean {
    return moment(date).isValid();
  }

  diff(to: string): number {
    return this.moment.diff(to);
  }

  roundTenMinutes() {
    const min = Math.floor(this.moment.minute() / 10) * 10;
    return this.moment.format(`YYYY/MM/DD HH:${fillZero(min, 2)}`);
  }

  getDateFormat(format: string): string {
    return this.moment.format(format);
  }

  getStartOfFormat(unit: moment.unitOfTime.StartOf, format: string): string {
    return this.moment.startOf(unit).format(format);
  }

  getEndOfFormat(unit: moment.unitOfTime.StartOf, format: string): string {
    return this.moment.endOf(unit).format(format);
  }

  static isBefore(
    fromDate: string,
    toDate: string,
    format: string = "YYYY/MM/DD HH:mm:ss"
  ): boolean {
    return moment(fromDate, format).isBefore(moment(toDate, format));
  }

  static convertApiDateTime(apiDateTime: string, format: string): string {
    return moment(apiDateTime, API_DATETIME_FORMAT).format(format);
  }

  // センサーからのデータが 10 分間隔で送信されてくるため、丸め込んだデータと一致する日付データの配列を作成する
  static createDatetimes(
    rawDate: string,
    unit: "day" | "week" | "month",
    startDate: string = "00:00",
    endDate: string = "23:50"
  ): string[] {
    let start = moment(rawDate, "YYYY-MM-DD 00:00").startOf(unit);
    let end = moment(rawDate, "YYYY-MM-DD 23:50").endOf(unit);
    // 日付の場合は時間指定出来るため、引数の startDate と endDate を使用する
    if (unit === "day") {
      start = moment(`${rawDate} ${startDate}`);
      end = moment(`${rawDate} ${endDate}`);
    }
    const res: string[] = [];
    while (start.isSameOrBefore(end)) {
      res.push(start.format("YYYY/MM/DD HH:mm"));
      start.add(10, "minutes");
    }
    return res;
  }

  // カレンダーに表示する日付配列を作成する
  static createCalendar(date: string): WeekRow[][] {
    const currentDate = moment(date);
    // カレンダー最初の日
    const startDate = moment(currentDate).startOf("month");
    const calStartDate = startDate.subtract(startDate.day(), "days");

    // カレンダー最後の日
    const endDate = moment(currentDate).endOf("month");
    const calEndDate = endDate.add(6 - endDate.day(), "days");

    // カレンダー縦の週の数
    const weekNumber = Math.ceil(calEndDate.diff(calStartDate, "days") / 7);

    const calendars = Array<Array<WeekRow>>();

    for (let week = 0; week < weekNumber; week++) {
      let weekRows = Array<WeekRow>();
      for (let day = 0; day < 7; day++) {
        let isThisMonth = false;
        if (calStartDate.get("month") != currentDate.get("month"))
          isThisMonth = true;
        weekRows.push({
          date: calStartDate.format("YYYYMMDD"),
          day: calStartDate.get("date"),
          isSelected: false,
          isThisMonth: isThisMonth
        });
        calStartDate.add(1, "days");
      }
      calendars.push(weekRows);
    }
    return calendars;
  }

  // static scheduleDiff(startDate: string, endDate: string): number {
  //   let s = moment(startDate);
  //   let e = moment(endDate);
  //   return e.diff(s, "days");
  // }
}
